import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';

/**
 * blockNonNumericPaste — onPaste handler for numeric `<input type="number">`
 * fields. Modern browsers already block letter key-presses outright on
 * number inputs (typing "abc" produces no characters), so the only
 * realistic user path that gets garbage into the field is pasting from
 * the clipboard.
 *
 * This handler:
 *   1. Tests the pasted text against a numeric regex (digits, optional
 *      single decimal point, optional leading minus).
 *   2. If non-numeric, calls preventDefault() so the field stays at its
 *      previous valid value AND fires a sonner toast explaining why
 *      the paste was rejected.
 *   3. If numeric, lets the paste proceed normally.
 *
 * Usage:
 *   <input type="number" onPaste={blockNonNumericPaste} ... />
 *
 * Toast cooldown: a single in-progress toast tracker prevents the
 * "stack of 5 identical toasts" feel if the user rapid-pastes garbage
 * across multiple fields.
 */
let _toastInFlight = false;

export function blockNonNumericPaste(e) {
  const cb = e.clipboardData || window.clipboardData;
  if (!cb) return;
  const text = (cb.getData('text') || '').trim();
  if (!text) return;

  // Allow: digits, optional single decimal, optional leading minus.
  // The UI inputs additionally clamp negatives to 0 via Math.max,
  // but we accept the paste so the user can correct typos like "-50".
  const numericRegex = /^-?\d*\.?\d*$/;
  if (numericRegex.test(text)) return;

  e.preventDefault();
  if (_toastInFlight) return;
  _toastInFlight = true;
  toast.error('Numbers only', {
    description: `Pasted "${text.slice(0, 24)}${text.length > 24 ? '…' : ''}" was rejected — this field accepts numbers only.`,
    duration: 2500,
  });
  // Free the cooldown slightly after the toast auto-dismisses so a
  // fresh paste attempt 3+ seconds later can re-trigger the warning.
  setTimeout(() => { _toastInFlight = false; }, 2700);
}

/**
 * NumericInput — controlled numeric input that ALLOWS transient
 * invalid display states during typing (empty string, "-", "1.", etc.)
 * without clobbering the user's typed value back to 0/min.
 *
 * Round 86c (May 2026) — Built to fix the mobile-keyboard bug where
 * `value={Math.max(0, Number(e.target.value) || 0)}` made it
 * impossible to:
 *   • Backspace a field empty (showed "0" instead of "")
 *   • Type a leading minus sign for legitimately negative fields
 *   • Clear the timeline field (locked at "1")
 *
 * Behaviour:
 *   • Display follows the user's raw typed string.
 *   • Numeric prop changes (e.g. parent re-renders with a different
 *     value) sync the display.
 *   • onChangeNumber fires with a parsed, clamped number ONLY when
 *     the typed string parses cleanly. While the user is typing
 *     "-" or "1." or "" the parent never sees an invalid number.
 *   • On blur, the field snaps to a valid value (min if empty, clamped
 *     to [min, max]).
 *   • Supports `integer` for whole-number fields (timeline).
 *   • `allowNegative` controls whether "-" is accepted at all.
 *
 * Props:
 *   value          — current numeric value (number)
 *   onChange       — receives the new numeric value (number)
 *   min, max       — clamp range applied on blur
 *   integer        — true for fields that must be whole numbers
 *   allowNegative  — true to let the user type a leading "-"
 *   className,
 *   data-testid,
 *   title,
 *   step,
 *   ...rest        — passed through to <input>
 */
export function NumericInput({
  value,
  onChange,
  min,
  max,
  integer = false,
  allowNegative = false,
  className,
  step,
  ...rest
}) {
  // Internal raw-string state so typing "-" or "" doesn't lose focus
  // or get rewritten by the parent's clamping.
  const [str, setStr] = useState(() => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return String(value);
  });
  // Track whether the field is currently focused so external `value`
  // updates only sync into the display when the user ISN'T typing.
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    if (value === null || value === undefined || Number.isNaN(value)) {
      setStr('');
    } else {
      // Only re-stringify when the parent's value disagrees with our
      // current parsed string (avoids "12" → 12 → "12" churn).
      const parsed = Number(str);
      if (!Number.isFinite(parsed) || parsed !== Number(value)) {
        setStr(String(value));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Accept any string the user types as long as it could *eventually*
  // parse into a number. Empty string, "-", "1.", "1.5", "0.5", etc.
  const isValidTransient = (s) => {
    if (s === '') return true;
    if (allowNegative && s === '-') return true;
    if (!allowNegative && s.startsWith('-')) return false;
    // Allow standard decimal patterns
    return /^-?\d*\.?\d*$/.test(s);
  };

  const handleChange = (e) => {
    const next = e.target.value;
    if (!isValidTransient(next)) return; // reject garbage like "12abc"
    setStr(next);
    const n = Number(next);
    if (Number.isFinite(n)) {
      let clamped = n;
      if (min != null) clamped = Math.max(min, clamped);
      if (max != null) clamped = Math.min(max, clamped);
      if (integer) clamped = Math.round(clamped);
      onChange?.(clamped);
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    // Snap to a valid number on blur
    let n = Number(str);
    if (!Number.isFinite(n)) n = min ?? 0;
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    if (integer) n = Math.round(n);
    onChange?.(n);
    setStr(String(n));
  };

  return (
    <input
      {...rest}
      // Use "text" with inputMode so mobile keyboards still show the
      // numeric keypad, but the field accepts "-" and stays mounted
      // when transiently empty (which type=number aggressively
      // bullies into "0" on iOS/Android browsers).
      type="text"
      inputMode={integer ? "numeric" : "decimal"}
      // `pattern` opens the iOS numeric keypad without the period.
      pattern={allowNegative ? "-?[0-9]*\\.?[0-9]*" : "[0-9]*\\.?[0-9]*"}
      value={str}
      onChange={handleChange}
      onFocus={() => { focusedRef.current = true; }}
      onBlur={handleBlur}
      onPaste={blockNonNumericPaste}
      className={className}
      step={step}
    />
  );
}
